import React from "react";

/**
 * AuditIQ-branded error boundary — spec §5/§6.
 * Catches React render errors and displays a navy/gold branded message.
 * Never lets a white screen / framework error appear in production.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV !== "production") console.error("[AuditIQ ErrorBoundary]", error, info?.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.href = "/dashboard";
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const message = this.state.error?.message || "An unexpected error occurred.";
    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          background: "#0d1f3c",
          color: "#f4efe6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "Merriweather, Georgia, serif",
        }}
      >
        <div
          style={{
            maxWidth: 560,
            border: "2px double #c9a84c",
            borderRadius: 10,
            padding: 32,
            background: "#112244",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontFamily: "Cinzel, Georgia, serif",
              color: "#c9a84c",
              fontSize: 28,
              letterSpacing: 2,
              margin: "0 0 12px 0",
            }}
          >
            System Error
          </h1>
          <p style={{ color: "#f4efe6", fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
            Our team has been notified. Please return to the dashboard or refresh.
          </p>
          <p
            style={{
              color: "rgba(244,239,230,0.55)",
              fontSize: 11,
              fontFamily: "ui-monospace, Menlo, monospace",
              wordBreak: "break-word",
              marginBottom: 24,
            }}
          >
            {message}
          </p>
          <button
            data-testid="error-boundary-reset"
            onClick={this.handleReset}
            style={{
              background: "#c9a84c",
              color: "#0d1f3c",
              border: "none",
              padding: "10px 20px",
              borderRadius: 4,
              fontFamily: "Cinzel, Georgia, serif",
              fontWeight: 600,
              letterSpacing: 1,
              cursor: "pointer",
              textTransform: "uppercase",
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
